import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const InstallAppButton = ({ className = '' }) => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Rilevamento dispositivo iPhone
  const isIPhone = /iPhone/.test(navigator.userAgent);
  const isStandalone = window.navigator.standalone === true;

  useEffect(() => {
    // Listener per l'evento beforeinstallprompt (PC, iPad, Android)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listener per verificare se l'app è già installata
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    // Verifica se siamo in modalità standalone (app già installata)
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches || 
          window.navigator.standalone === true) {
        setIsInstalled(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    checkIfInstalled();

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Gestione installazione normale (PC, iPad, Android)
  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);

    try {
      const choiceResult = await deferredPrompt.prompt();
      
      if (choiceResult.outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
      }
    } catch (error) {
      console.error('Errore durante l\'installazione:', error);
    } finally {
      setIsInstalling(false);
      setDeferredPrompt(null);
    }
  };

  // Gestione istruzioni iPhone
  const handleIOSInstructions = () => {
    setShowIOSInstructions(true);
  };

  // LOGICA IPHONE - Gestione separata
  if (isIPhone) {
    // Se app già installata su iPhone
    if (isStandalone) {
      return (
        <button
          disabled
          className={`
            inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white
            bg-green-600 cursor-not-allowed
            transition-colors duration-200
            ${className}
          `}
          aria-label={t('installApp.installed')}
          title={t('installApp.installed')}
        >
          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {t('installApp.installed')}
        </button>
      );
    }
    
    // Se app NON installata su iPhone - mostra istruzioni
    return (
      <>
        <button
          onClick={handleIOSInstructions}
          className={`
            inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white
            bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
            transition-colors duration-200
            ${className}
          `}
          aria-label={t('installApp.howToInstall')}
          title={t('installApp.howToInstall')}
        >
          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          {t('installApp.howToInstall')}
        </button>

        {/* Modal con istruzioni iOS */}
        {showIOSInstructions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('installApp.iosTitle')}
                </h3>
                <button
                  onClick={() => setShowIOSInstructions(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Chiudi"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4 text-sm text-gray-700">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
                    1
                  </div>
                  <p>{t('installApp.iosStep1')}</p>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
                    2
                  </div>
                  <p>{t('installApp.iosStep2')}</p>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
                    3
                  </div>
                  <p>{t('installApp.iosStep3')}</p>
                </div>
              </div>
              
              <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>{t('installApp.iosNote')}</strong> {t('installApp.iosNoteText')}
                </p>
              </div>
              
              <div className="mt-6">
                <button
                  onClick={() => setShowIOSInstructions(false)}
                  className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                  {t('installApp.iosClose')}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // LOGICA NORMALE - PC, iPad, Android
  // Non mostrare se non è installabile e non è installato
  if (!isInstallable && !isInstalled) {
    return null;
  }

  // Funzioni di supporto per pulsante normale
  const getButtonText = () => {
    if (isInstalling) return t('installApp.installing');
    if (isInstalled) return t('installApp.installed');
    return t('installApp.install');
  };

  const getButtonIcon = () => {
    if (isInstalling) {
      return (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      );
    }
    
    if (isInstalled) {
      return (
        <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }

    return (
      <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
      </svg>
    );
  };

  // Pulsante normale per PC, iPad, Android
  return (
    <button
      onClick={handleInstallClick}
      disabled={isInstalling || isInstalled}
      className={`
        inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white
        ${isInstalled 
          ? 'bg-green-600 cursor-not-allowed' 
          : 'bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500'
        }
        ${isInstalling ? 'cursor-not-allowed opacity-75' : ''}
        transition-colors duration-200
        ${className}
      `}
      aria-label={getButtonText()}
      title={getButtonText()}
    >
      {getButtonIcon()}
      {getButtonText()}
    </button>
  );
};

export default InstallAppButton;